import React, { useEffect, useMemo, useState } from 'react';
import BuildVersion from '../../models/enums/build-version';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectVersionsData } from '../../store/general/general-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getUpdateVersions, getVersionSets } from '../../store/general/general-actions';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { AddIcon, DownArrow, EditIcon, MergeIcon } from '@common/utils/icons/icons';
import CustomLabelTextTable from '../misc/CustomLabelTextTable';
import VersionSetForm from './VersionSetForm';
import VersionSet from '../../models/general/versions/version-set';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import { isHttpError } from '@common/interfaces/api/http-error';
import { resetVersionSetsRestState } from '../../store/general/general-slice';
import MenuItem from '@mui/material/MenuItem';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { sortByDate } from '@common/utils/date';
import VersionedPackage from '../../models/enums/versioned-package';
import PackageMode from '../../models/enums/package-mode';
import TorizonVersionSetsPanel from './TorizonVersionSetsPanel';

export const VersionsTab: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const versionsData = useAppSelector((state) => selectVersionsData(state));

  const [customBuildAnchorEl, setCustomBuildAnchorEl] = useState<HTMLDivElement | null>(null);
  const [buildVersion, setBuildVersion] = useState<string>(BuildVersion.MASTER);
  const [editedVersionSet, setEditedVersionSet] = useState<Partial<VersionSet> | null>(null);
  const [suggestedVersionSet, setSuggestedVersionSet] = useState<Partial<VersionSet> | null>(null);
  const [packageMode, setPackageMode] = useState<PackageMode>(PackageMode.DEBIAN);

  useEffect(() => {
    if (versionsData.versions.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getUpdateVersions());
    }
  }, [dispatch, versionsData.versions.rest.getStatus]);

  useEffect(() => {
    if (versionsData.sets.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getVersionSets());
    }
  }, [dispatch, versionsData.sets.rest.getStatus]);

  const buildVersionChangeHandler = (build: string) => {
    setBuildVersion(build);
  };

  const openCustomBuildPopoverClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    setCustomBuildAnchorEl(event.currentTarget);
  };

  const closeCustomBuildPopoverHandler = () => {
    setCustomBuildAnchorEl(null);
  };

  const closeVersionSetFormHandler = () => {
    setEditedVersionSet(null);
    setSuggestedVersionSet(null);
  };

  const getVersionById = (id: string | null | undefined) => {
    return versionsData.versions.data?.find((v) => v.id === id)?.version ?? t('info-not-set');
  };

  const editVersionSetClickHandler = (v: Partial<VersionSet>) => {
    closeCustomBuildPopoverHandler();
    setEditedVersionSet({ ...v });
  };

  const closeRestHandler = () => {
    dispatch(
      resetVersionSetsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  const currentVersionSet = versionsData.sets.data?.find((vs) => vs.tag === buildVersion);
  const isCustomVersionSet = !Object.values(BuildVersion)
    .flatMap((bv) => bv)
    .some((bv) => bv === buildVersion);

  const oldestSelectedVersionDate = useMemo(() => {
    const dates = versionsData.versions.data
      ?.filter((v) =>
        (versionsData.sets.data ?? [])
          .filter(
            (vs) =>
              vs.tag === BuildVersion.MASTER ||
              vs.tag === BuildVersion.STAGING ||
              vs.tag === BuildVersion.BETA,
          )
          .flatMap((vs) => [
            vs.debianMetaPackageId,
            vs.managementContainersId,
            vs.managementContainersId,
            vs.roboticsSoftwareId,
          ])
          .includes(v.id),
      )
      .flatMap((v) => v.dateCreated)
      .sort();
    return dates != null && dates.length > 0 ? dates[dates.length - 1] : null;
  }, [versionsData.sets.data, versionsData.versions.data]);

  const newVersions = versionsData.versions.data
    ?.filter((v) => oldestSelectedVersionDate == null || v.dateCreated > oldestSelectedVersionDate)
    .sort((a, b) => sortByDate(a.dateCreated, b.dateCreated));

  const addToSetClickHandler = () => {
    setEditedVersionSet({ ...currentVersionSet, tag: buildVersion });
    setSuggestedVersionSet({
      roboticsSoftwareId: newVersions?.find(
        (nv) => nv.package === VersionedPackage.ROBOTICS_SOFTWARE,
      )?.id,
      debianMetaPackageId: newVersions?.find(
        (nv) => nv.package === VersionedPackage.DEBIAN_META_PACKAGE,
      )?.id,
      managementContainersId: newVersions?.find(
        (nv) => nv.package === VersionedPackage.MANAGEMENT_CONTAINERS,
      )?.id,
    });
  };

  const copyFromSetClickHandler = () => {
    setEditedVersionSet({
      ...[...(versionsData.sets.data ?? [])]
        .sort((a, b) => sortByDate(a.dateCreated, b.dateCreated))
        .find((vs) => vs.tag !== buildVersion),
    });
    setSuggestedVersionSet({ ...currentVersionSet, tag: buildVersion });
  };

  const updateEditedVersionSetHandler = (newTag: string) => {
    setEditedVersionSet({ ...versionsData.sets.data?.find((vs) => vs.tag === newTag) });
  };

  const newVersionsForBanner = packageMode === PackageMode.DEBIAN ? newVersions : undefined;

  return (
    <React.Fragment>
      <Stack spacing={2} height="100%">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={packageMode}
          onChange={(_, value) => value != null && setPackageMode(value)}
        >
          <ToggleButton value={PackageMode.DEBIAN}>{t('button-debian-packages')}</ToggleButton>
          <ToggleButton value={PackageMode.TORIZON}>{t('button-torizon-packages')}</ToggleButton>
        </ToggleButtonGroup>
        {newVersionsForBanner != null && newVersionsForBanner.length > 0 && (
          <CustomAlert
            header={t('info-new-versions-available')}
            action={
              <Button startIcon={<MergeIcon />} onClick={addToSetClickHandler}>
                {t('button-add-versions-to-set')}
              </Button>
            }
          />
        )}
        {packageMode === PackageMode.TORIZON ? (
          <TorizonVersionSetsPanel />
        ) : (
          <React.Fragment>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
              {Object.values(BuildVersion).map((bv) => (
                <Chip
                  key={bv}
                  label={t('build-version-' + bv)}
                  color={buildVersion === bv ? 'primary' : undefined}
                  variant="outlined"
                  onClick={() => buildVersionChangeHandler(bv)}
                />
              ))}
              <Chip
                label={
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="inherit">
                      {isCustomVersionSet ? currentVersionSet?.tag : t('build-version-CUSTOM')}
                    </Typography>
                    <DownArrow fontSize="small" />
                  </Stack>
                }
                color={isCustomVersionSet ? 'primary' : undefined}
                variant="outlined"
                onClick={openCustomBuildPopoverClickHandler}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                startIcon={<EditIcon />}
                onClick={() =>
                  editVersionSetClickHandler({ ...currentVersionSet, tag: buildVersion })
                }
              >
                {t('button-edit')}
              </Button>
              <Button startIcon={<MergeIcon />} onClick={copyFromSetClickHandler}>
                {t('button-copy-versions-to-set')}
              </Button>
            </Stack>
            <CustomLabelTextTable
              rows={[
                {
                  label: t('table-header-robotics-software'),
                  text: getVersionById(currentVersionSet?.roboticsSoftwareId),
                },
                {
                  label: t('table-header-debian-meta-package'),
                  text: getVersionById(currentVersionSet?.debianMetaPackageId),
                },
                {
                  label: t('table-header-management-containers'),
                  text: getVersionById(currentVersionSet?.managementContainersId),
                },
              ]}
            />
          </React.Fragment>
        )}
      </Stack>
      <Popover
        id="custom-builds-popover-id"
        open={Boolean(customBuildAnchorEl)}
        anchorEl={customBuildAnchorEl}
        onClose={closeCustomBuildPopoverHandler}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Button size="large" startIcon={<AddIcon />} onClick={() => editVersionSetClickHandler({})}>
          {t('button-create-version-set')}
        </Button>
        {versionsData.sets.data
          ?.filter((vs) => !Object.values(BuildVersion).some((bv) => bv === vs.tag))
          .map((vs) => (
            <MenuItem
              key={vs.id}
              value={vs.tag}
              onClick={() => {
                setBuildVersion(vs.tag);
                closeCustomBuildPopoverHandler();
              }}
            >
              {vs.tag}
            </MenuItem>
          ))}
      </Popover>
      {editedVersionSet != null && (
        <VersionSetForm
          versionSet={editedVersionSet}
          suggestedSet={suggestedVersionSet ?? undefined}
          onClose={closeVersionSetFormHandler}
          setBuildVersion={buildVersionChangeHandler}
          setEditedVersion={updateEditedVersionSetHandler}
        />
      )}
      <CustomSnackbar
        open={versionsData.sets.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-version')}
        onClose={closeRestHandler}
      />
      <CustomSnackbar
        open={versionsData.sets.rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-version')}
        onClose={closeRestHandler}
      />
      <CustomSnackbar
        open={versionsData.sets.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-version')}
        onClose={closeRestHandler}
      />
      <CustomSnackbar
        open={versionsData.sets.rest.deleteStatus === HttpFetchStatus.Failed}
        title={t('error-deleting-version')}
        onClose={closeRestHandler}
        severity="error"
        textMessages={
          versionsData.sets.rest.deleteError != null &&
          isHttpError(versionsData.sets.rest.deleteError)
            ? [versionsData.sets.rest.deleteError.message]
            : undefined
        }
      />
    </React.Fragment>
  );
};

export default VersionsTab;
